// src/components/blog/features/BlogPostInteractive.tsx

'use client'

import { ReadingProgress } from '~/components/blog/features/ReadingProgress'
import { AnimatedTableOfContents } from '~/components/blog/features/AnimatedTableOfContents'
import type { TableOfContentsItem } from '~/lib/blog/types'
import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { cn } from '~/lib/utils'
import { useNavbarHeight } from '~/lib/hooks/useNavbarHeight'

/**
 * Props for the BlogPostInteractive component
 * 
 * @interface BlogPostInteractiveProps
 * @property {TableOfContentsItem[]} tableOfContents - Array of TOC items to display
 */
interface BlogPostInteractiveProps {
  tableOfContents: TableOfContentsItem[]
}

/**
 * Layout configuration constants
 * 
 * @constant
 * @property {number} TOP_BUFFER - Space between navbar and TOC (in pixels)
 * @property {number} BOTTOM_BUFFER - Space at bottom of viewport (in pixels)
 * @property {number} SCROLL_THRESHOLD - Percentage of viewport height before showing TOC (0.25 = 25%)
 */
const LAYOUT_CONFIG = {
  TOP_BUFFER: 30,
  BOTTOM_BUFFER: 50,
  SCROLL_THRESHOLD: 0.25
} as const

/**
 * Custom hook for managing scroll-based UI behavior
 * 
 * Tracks two states:
 * 1. Whether user has scrolled past threshold (to show TOC)
 * 2. Whether TOC should be visible (hidden when related posts section is in view)
 * 
 * Uses requestAnimationFrame for scroll throttling (better than setTimeout):
 * - Automatically syncs with display refresh rate (~60fps)
 * - Only runs when tab is visible (better battery life)
 * - Prevents layout thrashing by batching with repaints
 * 
 * Caches DOM references to avoid repeated querySelector calls during scroll
 * 
 * @returns {Object} Object containing isScrolled and showToc boolean states
 *   @returns {boolean} .isScrolled - Whether user has scrolled past threshold
 *   @returns {boolean} .showToc - Whether TOC should be visible
 */
function useScrollBehavior(): { isScrolled: boolean; showToc: boolean } {
  const [isScrolled, setIsScrolled] = useState(false)
  const [showToc, setShowToc] = useState(true)
  
  /**
   * Cached reference to the related posts section
   * Using useRef prevents repeated DOM queries on every scroll event
   * This is a significant performance optimization for scroll handlers
   */
  const relatedSectionRef = useRef<HTMLElement | null>(null)

  /**
   * Updates scroll-based UI states
   * Checks scroll position and related section visibility
   */
  const updateScrollState = useCallback(() => {
    if (typeof window === 'undefined') return

    // Cache the related section reference on first use
    // Uses nullish coalescing assignment (??=) to only query once
    relatedSectionRef.current ??=
      document.querySelector<HTMLElement>('section[data-related-posts]') ??
      document.querySelector<HTMLElement>('section.mt-12')

    // Check if scrolled past threshold
    const threshold = window.innerHeight * LAYOUT_CONFIG.SCROLL_THRESHOLD
    setIsScrolled(window.scrollY > threshold)

    // Check if related posts section is visible
    const relatedSection = relatedSectionRef.current
    if (relatedSection) {
      const rect = relatedSection.getBoundingClientRect()
      // Hide TOC when related section enters viewport
      setShowToc(rect.top > window.innerHeight)
    } else {
      // If section not found, keep TOC visible
      setShowToc(true)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Track requestAnimationFrame ID for cleanup
    let frameId: number | null = null
    // ResizeObserver for the related section (in case it changes size)
    let resizeObserver: ResizeObserver | null = null
    // Track which section we're observing
    let observedSection: HTMLElement | null = null

    /**
     * Ensures the related section is being observed for size changes
     * Only creates/updates observer when necessary
     */
    const ensureObserver = (): void => {
      const section = relatedSectionRef.current
      if (!section) return

      // Don't recreate observer if already observing the same section
      if (resizeObserver && observedSection === section) return

      // Clean up old observer and create new one
      resizeObserver?.disconnect()
      resizeObserver = new ResizeObserver(() => updateScrollState())
      resizeObserver.observe(section)
      observedSection = section
    }

    /**
     * Runs the scroll state update and ensures observer is set up
     */
    const runUpdate = (): void => {
      updateScrollState()
      ensureObserver()
    }

    /**
     * Scroll event handler with requestAnimationFrame throttling
     * Only schedules one frame at a time to prevent queue buildup
     * This is more efficient than setTimeout-based debouncing
     */
    const onScroll = (): void => {
      // Don't schedule if already scheduled
      if (frameId !== null) return

      frameId = window.requestAnimationFrame(() => {
        frameId = null
        runUpdate()
      })
    }

    // Set up event listeners
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    window.addEventListener('orientationchange', onScroll)

    // Initial update
    runUpdate()

    // Cleanup function
    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }

      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      window.removeEventListener('orientationchange', onScroll)
      resizeObserver?.disconnect()
    }
  }, [updateScrollState])

  return { isScrolled, showToc }
}

/**
 * Custom hook for managing scroll restoration behavior
 * 
 * Disables browser's automatic scroll restoration and resets scroll position
 * This ensures consistent behavior across page navigations
 * Restores original behavior on unmount
 */
function useScrollRestoration(): void {
  useEffect(() => {
    const originalScrollRestoration = history.scrollRestoration

    // Reset scroll position to top
    window.scrollTo(0, 0)
    
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }

    // Restore original behavior on cleanup
    return () => {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = originalScrollRestoration
      }
    }
  }, [])
}

/**
 * BlogPostInteractive Component
 * 
 * Manages interactive features for blog posts:
 * - Reading progress indicator
 * - Animated table of contents (desktop only)
 * 
 * Architecture:
 * - Uses shared useNavbarHeight() hook for consistent navbar height calculation
 * - Passes navbar height as prop to AnimatedTableOfContents for scroll offset
 * - Single source of truth for navbar height prevents duplication and inconsistencies
 * 
 * Performance optimizations:
 * - Uses shared ResizeObserver via useNavbarHeight hook (no duplication)
 * - Uses requestAnimationFrame for scroll throttling
 * - Caches DOM references to avoid repeated queries
 * - Memoizes expensive calculations
 * 
 * @param {BlogPostInteractiveProps} props - Component props
 * @returns {JSX.Element} The interactive blog post features
 * 
 * @example
 * ```tsx
 * <BlogPostInteractive
 *   tableOfContents={tocItems}
 * />
 * ```
 */
export function BlogPostInteractive({
  tableOfContents
}: BlogPostInteractiveProps): JSX.Element {
  // Get total navbar height from shared hook
  // This is the single source of truth for navbar height calculation
  const totalNavbarHeight = useNavbarHeight()
  
  // Get scroll-based UI states
  const { isScrolled, showToc } = useScrollBehavior()
  
  // Manage scroll restoration behavior
  useScrollRestoration()

  /**
   * Memoized layout calculations for TOC positioning
   * Recalculates only when navbar height changes
   * 
   * @property {number} topPosition - Top position for TOC container
   * @property {string} maxHeight - Maximum height for TOC container
   */
  const layoutConfig = useMemo(() => ({
    topPosition: totalNavbarHeight + LAYOUT_CONFIG.TOP_BUFFER,
    maxHeight: `calc(100dvh - ${totalNavbarHeight + LAYOUT_CONFIG.TOP_BUFFER + LAYOUT_CONFIG.BOTTOM_BUFFER}px)`
  }), [totalNavbarHeight])

  /**
   * Memoized filtered table of contents
   * Only includes h2 and h3 headings (levels 2-3)
   * Recalculates only when tableOfContents prop changes
   */
  const filteredTableOfContents = useMemo(
    () => tableOfContents.filter(item => item.level >= 2 && item.level <= 3),
    [tableOfContents]
  )

  return (
    <>
      {/* Reading Progress - uses Framer Motion's useScroll internally */}
      <ReadingProgress />

      {/* The article page owns its single floating share control. */}

      {/* Table of Contents - Desktop Only */}
      <div className="pointer-events-none fixed inset-y-0 left-0 z-20 hidden w-72 lg:block">
        <div 
          className={cn(
            "absolute w-full px-6 transition-all duration-300",
            isScrolled && showToc
              ? "opacity-100 translate-y-0 pointer-events-auto" 
              : "opacity-0 translate-y-4 pointer-events-none"
          )}
          style={{ 
            top: `${layoutConfig.topPosition}px`,
            maxHeight: layoutConfig.maxHeight
          }}
        >
          <div className="w-full">
            {/* Pass navbar height as prop for scroll offset calculation */}
            <AnimatedTableOfContents 
              items={filteredTableOfContents}
              navbarHeight={totalNavbarHeight}
            />
          </div>
        </div>
      </div>
    </>
  )
}